import React, { useMemo, useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useContext } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './TbsPdfReader.css';
import { useTranslation } from 'react-i18next';
import { FreeModalContext } from './FreeModal';
import sidebarToggleIcon from '../../../res/images/pdf-toolbar-icons/toolbarButton-sidebarToggle.png';
import pageDownIcon from '../../../res/images/pdf-toolbar-icons/toolbarButton-pageDown.png';
import pageUpIcon from '../../../res/images/pdf-toolbar-icons/toolbarButton-pageUp.png';
import secondaryToolbarToggleIcon from '../../../res/images/pdf-toolbar-icons/toolbarButton-secondaryToolbarToggle.png';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/js/pdf.worker.min.mjs`;

const getStorageKey = (docUrl, instanceId) => `pdfHighlights_${instanceId || docUrl}`;
const getStrikethroughStorageKey = (docUrl, instanceId) => `pdfStrikethroughs_${instanceId || docUrl}`;

function debounce(fn, ms) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            fn.apply(this, args);
        }, ms);
    };
}

const getPathTo = (node, root) => {
    if (node === root) return '';
    if (!root.contains(node)) return null;
    const path = [];
    let current = node;
    while (current !== root) {
        const parent = current.parentNode;
        if (!parent) return null;
        const index = Array.from(parent.childNodes).indexOf(current);
        path.unshift(index);
        current = parent;
    }
    return path.join('.');
};

const comparePaths = (path1, offset1, path2, offset2) => {
    if (path1 === path2) {
        return offset1 - offset2;
    }
    
    const parts1 = path1.split('.').map(Number);
    const parts2 = path2.split('.').map(Number);
    
    for (let i = 0; i < Math.min(parts1.length, parts2.length); i++) {
        if (parts1[i] !== parts2[i]) {
            return parts1[i] - parts2[i];
        }
    }
    
    return parts1.length - parts2.length;
};

const getNodeFromPath = (path, root) => {
    if (!path) return null;
    if (path === '') return root;
    try {
        return path.split('.').reduce((current, index) => {
            if (!current || !current.childNodes) {
                return null;
            }
            return current.childNodes[parseInt(index, 10)];
        }, root);
    } catch (error) {
        console.warn('Could not find node from path:', path, error);
        return null;
    }
};

const TbsPdfReader = forwardRef(({ docUrl, showTools = true, disableFontFace = true, width, height, advancedTools = false, instanceId = null, scrolling }, ref) => {
    const { t } = useTranslation();
    const freeModalWidth = useContext(FreeModalContext);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1);
    const [selectedText, setSelectedText] = useState('');
    const [zoomMode, setZoomMode] = useState('auto');
    const [pdfDocument, setPdfDocument] = useState(null);
    const [autoWidth, setAutoWidth] = useState(null);
    const [autoHeight, setAutoHeight] = useState(null);
    const pageRef = useRef(null);
    
    // UI state
    const [showSidebar, setShowSidebar] = useState(false);
    const [showSearchBar, setShowSearchBar] = useState(false);
    const [showZoomDropdown, setShowZoomDropdown] = useState(false);
    const [showToolsMenu, setShowToolsMenu] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    
    // Advanced tools state
    const [activeTool, setActiveTool] = useState(null); // 'highlight' or 'strikethrough'
    const [highlightColor, setHighlightColor] = useState('yellow');
    
    // Search state
    const [searchText, setSearchText] = useState('');
    const [highlightAll, setHighlightAll] = useState(false);
    const [matchCase, setMatchCase] = useState(false);
    const [searchMatches, setSearchMatches] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    
    // Rotation state
    const [pageRotations, setPageRotations] = useState({});
    
    const [highlightBoxes, setHighlightBoxes] = useState([]);
    const [strikethroughBoxes, setStrikethroughBoxes] = useState([]);
    const pdfContainerRef = useRef(null);
    const searchInputRef = useRef(null);

    const [highlights, setHighlights] = useState([]);
    const [strikethroughs, setStrikethroughs] = useState([]);
    const hasLoadedRef = useRef(false);
    const hasLoadedStrikethroughsRef = useRef(false);

    const options = useMemo(() => ({ disableFontFace }), [disableFontFace]);

    useEffect(() => {
        if (pdfContainerRef.current) {
            let parent = pdfContainerRef.current.parentElement;
            while (parent) {
                if (parent.style.direction === 'rtl') {
                    parent.style.direction = 'ltr';
                }
                parent = parent.parentElement;
            }
        }
    }, []);

    // Load highlights
    useEffect(() => {
        if (!docUrl) {
            setHighlights([]);
            hasLoadedRef.current = true;
            return;
        }

        const storageKey = getStorageKey(docUrl, instanceId);
        try {
            const saved = localStorage.getItem(storageKey);
            setHighlights(saved ? JSON.parse(saved) : []);
        } catch (error) {
            console.error("Failed to load highlights from localStorage:", error);
            setHighlights([]);
        }
        hasLoadedRef.current = true;
    }, [docUrl, instanceId]);

    // Save highlights
    useEffect(() => {
        if (!hasLoadedRef.current || !docUrl) return;
        const storageKey = getStorageKey(docUrl, instanceId);
        localStorage.setItem(storageKey, JSON.stringify(highlights));
    }, [highlights, docUrl, instanceId]);

    // Load strikethroughs
    useEffect(() => {
        if (!docUrl) {
            setStrikethroughs([]);
            hasLoadedStrikethroughsRef.current = true;
            return;
        }

        const storageKey = getStrikethroughStorageKey(docUrl, instanceId);
        try {
            const saved = localStorage.getItem(storageKey);
            setStrikethroughs(saved ? JSON.parse(saved) : []);
        } catch (error) {
            console.error("Failed to load strikethroughs from localStorage:", error);
            setStrikethroughs([]);
        }
        hasLoadedStrikethroughsRef.current = true;
    }, [docUrl, instanceId]);

    // Save strikethroughs
    useEffect(() => {
        if (!hasLoadedStrikethroughsRef.current || !docUrl) return;
        const storageKey = getStrikethroughStorageKey(docUrl, instanceId);
        localStorage.setItem(storageKey, JSON.stringify(strikethroughs));
    }, [strikethroughs, docUrl, instanceId]);

    const onDocumentLoadSuccess = (pdf) => {
        setNumPages(pdf.numPages);
        setPageNumber(1);
        setPdfDocument(pdf);
    };

    const onDocumentLoadError = (error) => {
        console.log("pdf", error.message);
    };

    const stableDocUrl = useMemo(() => docUrl, []);
    
    // Calculate automatic zoom based on container size
    const calculateAutoZoom = useCallback(async () => {
        if (!pdfContainerRef.current || zoomMode !== 'auto' || !pdfDocument) return;
        
        const container = pdfContainerRef.current;
        
        try {
            // Get the PDF page to access its viewport
            const pdfPage = await pdfDocument.getPage(pageNumber);
            const viewport = pdfPage.getViewport({ scale: 1 }); // Get viewport at 100% scale
            
            const containerRect = container.getBoundingClientRect();
            
            // Calculate available space (accounting for padding/margins)
            const availableWidth = containerRect.width - 40; // Account for padding
            const availableHeight = containerRect.height - 40; // Account for padding
            
            // Calculate scale to fit both width and height
            const scaleX = availableWidth / viewport.width;
            const scaleY = availableHeight / viewport.height;
            
            const fitScale = height === 'auto' ? Math.min(scaleX, 4) : Math.min(scaleX, scaleY, 4);
            
            // Calculate width and height based on the fit scale
            const calculatedWidth = viewport.width * fitScale;
            const calculatedHeight = viewport.height * fitScale;
            
            // Only update if the dimensions changed significantly (avoid infinite loops)
            setAutoWidth(prev => {
                if (Math.abs(calculatedWidth - (prev || 0)) > 1) {
                    return calculatedWidth;
                }
                return prev;
            });
            setAutoHeight(prev => {
                if (Math.abs(calculatedHeight - (prev || 0)) > 1) {
                    return calculatedHeight;
                }
                return prev;
            });
            // Set scale to 1 when using calculated width/height
            setScale(prev => {
                if (Math.abs(prev - 1) > 0.01) {
                    return 1;
                }
                return prev;
            });
        } catch (error) {
            console.warn('Failed to calculate auto zoom:', error);
        }
    }, [zoomMode, pdfDocument, pageNumber, height]);

    // Recalculate auto zoom when zoom mode changes to auto, page changes, or modal width changes
    useEffect(() => {
        if (zoomMode === 'auto' && pdfDocument) {
            // Wait for page to render before calculating
            calculateAutoZoom();
        }
    }, [zoomMode, pageNumber, pdfDocument, freeModalWidth, calculateAutoZoom]);
    
    // Zoom functions
    const zoomIn = () => {
        setScale(prev => Math.min(prev + 0.1, 4));
        setZoomMode('custom');
        setAutoWidth(null);
        setAutoHeight(null);
    };

    const zoomOut = () => {
        setScale(prev => Math.max(prev - 0.1, 0.25));
        setZoomMode('custom');
        setAutoWidth(null);
        setAutoHeight(null);
    };

    const setZoomLevel = (level) => {
        switch(level) {
            case 'auto':
                setZoomMode('auto');
                // Scale and dimensions will be calculated by calculateAutoZoom
                break;
            case 'actual':
                setZoomMode('actual');
                setScale(1);
                setAutoWidth(null);
                setAutoHeight(null);
                break;
            case 'fit':
                setZoomMode('fit');
                setScale(1);
                setAutoWidth(null);
                setAutoHeight(null);
                break;
            case 'width':
                setZoomMode('width');
                setScale(1);
                setAutoWidth(null);
                setAutoHeight(null);
                break;
            default:
                setZoomMode('custom');
                setScale(parseFloat(level) / 100);
                setAutoWidth(null);
                setAutoHeight(null);
        }
        setShowZoomDropdown(false);
    };

    // Page navigation
    const goToPage = (page) => {
        const pageNum = parseInt(page, 10);
        if (pageNum >= 1 && pageNum <= numPages) {
            setPageNumber(pageNum);
        }
    };

    const previousPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    };

    const nextPage = () => {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    };

    const goToFirstPage = () => {
        setPageNumber(1);
        setShowToolsMenu(false);
    };

    const goToLastPage = () => {
        setPageNumber(numPages);
        setShowToolsMenu(false);
    };

    // Rotation functions
    const rotateClockwise = () => {
        setPageRotations(prev => ({
            ...prev,
            [pageNumber]: ((prev[pageNumber] || 0) + 90) % 360
        }));
        setShowToolsMenu(false);
    };

    const rotateAntiClockwise = () => {
        setPageRotations(prev => ({
            ...prev,
            [pageNumber]: ((prev[pageNumber] || 0) - 90 + 360) % 360
        }));
        setShowToolsMenu(false);
    };

    // Search toggle
    const toggleSearch = () => {
        setShowSearchBar(!showSearchBar);
        if (!showSearchBar) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        } else {
            // Clear search when closing
            setSearchText('');
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
        }
    };

    // Search functionality - find matches in text layer
    const performSearch = useCallback(() => {
        if (!pdfContainerRef.current || !searchText.trim()) {
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
            return;
        }

        const matches = [];
        const searchQuery = matchCase ? searchText : searchText.toLowerCase();
        
        // Search through all pages
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
            const pageElement = pdfContainerRef.current.querySelector(`.react-pdf__Page[data-page-number="${pageNum}"]`);
            const textLayerElement = pageElement?.querySelector('.react-pdf__Page__textContent');
            
            if (!textLayerElement) continue;
            
            const textSpans = textLayerElement.querySelectorAll('span');
            
            textSpans.forEach((span, spanIndex) => {
                const text = span.textContent || '';
                const textToSearch = matchCase ? text : text.toLowerCase();
                
                let index = 0;
                while ((index = textToSearch.indexOf(searchQuery, index)) !== -1) {
                    matches.push({
                        pageNumber: pageNum,
                        spanIndex: spanIndex,
                        span: span,
                        startOffset: index,
                        endOffset: index + searchQuery.length,
                        text: text.substring(index, index + searchQuery.length)
                    });
                    index += searchQuery.length;
                }
            });
        }

        setSearchMatches(matches);
        if (matches.length > 0) {
            setCurrentMatchIndex(0);
            // Navigate to first match
            const firstMatch = matches[0];
            if (firstMatch.pageNumber !== pageNumber) {
                setPageNumber(firstMatch.pageNumber);
            }
        } else {
            setCurrentMatchIndex(-1);
        }
    }, [searchText, matchCase, numPages, pageNumber]);

    // Search navigation
    const findNext = useCallback(() => {
        if (searchMatches.length === 0) return;
        
        const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
        setCurrentMatchIndex(nextIndex);
        
        const match = searchMatches[nextIndex];
        if (match.pageNumber !== pageNumber) {
            setPageNumber(match.pageNumber);
        }
    }, [searchMatches, currentMatchIndex, pageNumber]);

    const findPrevious = useCallback(() => {
        if (searchMatches.length === 0) return;
        
        const prevIndex = (currentMatchIndex - 1 + searchMatches.length) % searchMatches.length;
        setCurrentMatchIndex(prevIndex);
        
        const match = searchMatches[prevIndex];
        if (match.pageNumber !== pageNumber) {
            setPageNumber(match.pageNumber);
        }
    }, [searchMatches, currentMatchIndex, pageNumber]);

    // Trigger search when search text or options change, or when page loads
    useEffect(() => {
        if (searchText.trim()) {
            const timeoutId = setTimeout(() => {
                performSearch();
            }, 300); // Debounce search
            return () => clearTimeout(timeoutId);
        } else {
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
        }
    }, [searchText, matchCase, performSearch]);

    // Re-run search when page changes or renders (to get text layer)
    useEffect(() => {
        if (searchText.trim() && pdfContainerRef.current) {
            const timeoutId = setTimeout(() => {
                performSearch();
            }, 100); // Wait for text layer to render
            return () => clearTimeout(timeoutId);
        }
    }, [pageNumber, searchText, performSearch]);

    // Apply search highlighting to text layer
    useEffect(() => {
        if (!pdfContainerRef.current) return;

        // Remove all existing search highlights
        const allMarks = pdfContainerRef.current.querySelectorAll('mark.search-highlight');
        allMarks.forEach(mark => {
            const parent = mark.parentNode;
            if (parent) {
                parent.replaceChild(document.createTextNode(mark.textContent), mark);
                parent.normalize(); // Merge adjacent text nodes
            }
        });

        if (searchMatches.length === 0) return;

        // Apply highlighting based on "Highlight all" setting
        searchMatches.forEach((match, index) => {
            if (!match.span || !match.span.textContent) return;
            
            const isCurrentMatch = index === currentMatchIndex;
            
            // If "Highlight all" is checked, highlight all matches
            // Otherwise, only highlight the current match
            if (highlightAll || isCurrentMatch) {
                const text = match.span.textContent;
                const startOffset = match.startOffset;
                const endOffset = match.endOffset;
                
                // Create text nodes and mark element
                const beforeText = text.substring(0, startOffset);
                const matchText = text.substring(startOffset, endOffset);
                const afterText = text.substring(endOffset);
                
                // Clear the span
                match.span.textContent = '';
                
                // Add before text
                if (beforeText) {
                    match.span.appendChild(document.createTextNode(beforeText));
                }
                
                // Add highlighted match
                const mark = document.createElement('mark');
                mark.className = isCurrentMatch ? 'search-highlight current-match' : 'search-highlight';
                mark.textContent = matchText;
                match.span.appendChild(mark);
                
                // Add after text
                if (afterText) {
                    match.span.appendChild(document.createTextNode(afterText));
                }
            }
        });

        // Scroll current match into view
        if (currentMatchIndex >= 0 && searchMatches[currentMatchIndex]) {
            const currentMatch = searchMatches[currentMatchIndex];
            if (currentMatch.span) {
                const mark = currentMatch.span.querySelector('mark.current-match');
                if (mark) {
                    mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    currentMatch.span.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        }
    }, [searchMatches, currentMatchIndex, highlightAll]);

    // Helper function for position calculations
    const calculatePositionRelativeToPage = useCallback((rect, pageRect) => {
        return {
            top: rect.top - pageRect.top,
            left: rect.left - pageRect.left
        };
    }, []);

    const updateHighlightBoxes = useCallback(() => {
        if (!pdfContainerRef.current) return;
        
        const allBoxes = [];

        highlights.forEach(highlight => {
            const pageElement = pdfContainerRef.current.querySelector(`.react-pdf__Page[data-page-number="${highlight.pageNumber}"]`);
            if (!pageElement) return;
            
            const pageRect = pageElement.getBoundingClientRect();
            const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');
            if (!textLayerElement) return;
            
            const startNode = getNodeFromPath(highlight.startPath, textLayerElement);
            const endNode = getNodeFromPath(highlight.endPath, textLayerElement);
            if (!startNode || !endNode) return;

            try {
                const range = document.createRange();
                range.setStart(startNode, highlight.startOffset);
                range.setEnd(endNode, highlight.endOffset);
                const rects = Array.from(range.getClientRects());
                
                rects.forEach((rect, index) => {
                    const position = calculatePositionRelativeToPage(rect, pageRect);
                    
                    allBoxes.push({
                        key: `${highlight.id}-${index}`,
                        pageNumber: highlight.pageNumber,
                        top: position.top,
                        left: position.left,
                        width: rect.width,
                        height: rect.height,
                        color: highlight.color || 'yellow',
                    });
                });
            } catch (error) {
                if (error.name !== 'IndexSizeError') {
                    console.error("Error rendering highlight:", error);
                }
            }
        });
        
        setHighlightBoxes(allBoxes);
    }, [highlights, calculatePositionRelativeToPage]);

    const updateStrikethroughBoxes = useCallback(() => {
        if (!pdfContainerRef.current) return;
        
        const allBoxes = [];

        strikethroughs.forEach(strikethrough => {
            const pageElement = pdfContainerRef.current.querySelector(`.react-pdf__Page[data-page-number="${strikethrough.pageNumber}"]`);
            if (!pageElement) return;
            
            const pageRect = pageElement.getBoundingClientRect();
            const textLayerElement = pageElement?.querySelector('.react-pdf__Page__textContent');
            if (!textLayerElement) return;
            
            const startNode = getNodeFromPath(strikethrough.startPath, textLayerElement);
            const endNode = getNodeFromPath(strikethrough.endPath, textLayerElement);
            if (!startNode || !endNode) return;

            try {
                const range = document.createRange();
                range.setStart(startNode, strikethrough.startOffset);
                range.setEnd(endNode, strikethrough.endOffset);
                const rects = Array.from(range.getClientRects());
                
                rects.forEach((rect, index) => {
                    const position = calculatePositionRelativeToPage(rect, pageRect);
                    
                    allBoxes.push({
                        key: `${strikethrough.id}-${index}`,
                        pageNumber: strikethrough.pageNumber,
                        top: position.top,
                        left: position.left,
                        width: rect.width,
                        height: rect.height,
                    });
                });
            } catch (error) {
                if (error.name !== 'IndexSizeError') {
                    console.error("Error rendering strikethrough:", error);
                }
            }
        });
        
        setStrikethroughBoxes(allBoxes);
    }, [strikethroughs, calculatePositionRelativeToPage]);

    useEffect(() => {
        const timer = setTimeout(() => {
            updateHighlightBoxes();
            updateStrikethroughBoxes();
        }, 200); // Increased delay to ensure text layer is fully rendered, especially for RTL
        return () => clearTimeout(timer);
    }, [highlights, strikethroughs, scale, numPages, pageNumber, updateHighlightBoxes, updateStrikethroughBoxes]);

    useEffect(() => {
        if (!pdfContainerRef.current) return;
        
        let rafId = null;
        const debouncedUpdate = debounce(() => {
            updateHighlightBoxes();
            updateStrikethroughBoxes();
        }, 50);

        const observer = new ResizeObserver(() => {
            // Update highlights/strikethroughs with debounce (expensive operations)
            debouncedUpdate();
            
            // Immediately recalculate auto zoom if in auto mode (use requestAnimationFrame for smooth updates)
            if (zoomMode === 'auto') {
                if (rafId !== null) {
                    cancelAnimationFrame(rafId);
                }
                rafId = requestAnimationFrame(() => {
                    calculateAutoZoom();
                    rafId = null;
                });
            }
        });

        observer.observe(pdfContainerRef.current);
        return () => {
            observer.disconnect();
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [updateHighlightBoxes, updateStrikethroughBoxes, zoomMode, calculateAutoZoom]);

    const handleHighlightSelected = (color = 'yellow') => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        const text = selection.toString();
        if (!text.trim()) return;

        const startNode = range.startContainer;
        const startSearchElement = startNode.nodeType === Node.TEXT_NODE ? startNode.parentNode : startNode;
        const startPageElement = startSearchElement.closest('.react-pdf__Page');
    
        const endNode = range.endContainer;
        const endSearchElement = endNode.nodeType === Node.TEXT_NODE ? endNode.parentNode : endNode;
        const endPageElement = endSearchElement.closest('.react-pdf__Page');
    
        if (!startPageElement || !endPageElement || startPageElement !== endPageElement) {
            alert("Highlighting across multiple pages is not supported. Please select text on a single page.");
            selection.removeAllRanges();
            return;
        }
    
        const pageElement = startPageElement;
        const pageNum = pageElement.getAttribute('data-page-number');
        const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');

        if (!pageNum || !textLayerElement) {
            alert('Could not process the highlight. The page structure is unexpected.');
            return;
        }

        const selectionStartPath = getPathTo(range.startContainer, textLayerElement);
        const selectionEndPath = getPathTo(range.endContainer, textLayerElement);
        const selectionStartOffset = range.startOffset;
        const selectionEndOffset = range.endOffset;

        const overlappingHighlights = highlights.filter(highlight => {
            if (highlight.pageNumber !== parseInt(pageNum, 10)) return false;
            
            const highlightStartVsSelectionEnd = comparePaths(highlight.startPath, highlight.startOffset, selectionEndPath, selectionEndOffset);
            const highlightEndVsSelectionStart = comparePaths(highlight.endPath, highlight.endOffset, selectionStartPath, selectionStartOffset);
            
            return highlightStartVsSelectionEnd < 0 && highlightEndVsSelectionStart > 0;
        });

        if (overlappingHighlights.length > 0) {
            const allSameColor = overlappingHighlights.every(h => h.color === color);
            
            if (allSameColor) {
                setHighlights(prev => {
                    const newHighlights = [];
                    const overlappingIds = overlappingHighlights.map(h => h.id);
                    
                    prev.forEach(highlight => {
                        if (overlappingIds.includes(highlight.id)) {
                            const highlightStartComparison = comparePaths(highlight.startPath, highlight.startOffset, selectionStartPath, selectionStartOffset);
                            const highlightEndComparison = comparePaths(highlight.endPath, highlight.endOffset, selectionEndPath, selectionEndOffset);
                            
                            if (highlightStartComparison < 0) {
                                newHighlights.push({
                                    ...highlight,
                                    id: highlight.id + '_before_' + Date.now(),
                                    endPath: selectionStartPath,
                                    endOffset: selectionStartOffset
                                });
                            }
                            
                            if (highlightEndComparison > 0) {
                                newHighlights.push({
                                    ...highlight,
                                    id: highlight.id + '_after_' + Date.now(),
                                    startPath: selectionEndPath,
                                    startOffset: selectionEndOffset
                                });
                            }
                        } else {
                            newHighlights.push(highlight);
                        }
                    });
                    
                    return newHighlights;
                });
            } else {
                setHighlights(prev => {
                    const newHighlights = [];
                    const overlappingIds = overlappingHighlights.map(h => h.id);
                    
                    prev.forEach(highlight => {
                        if (highlight.pageNumber !== parseInt(pageNum, 10)) {
                            newHighlights.push(highlight);
                            return;
                        }
                        
                        if (overlappingIds.includes(highlight.id)) {
                            const highlightStartComparison = comparePaths(highlight.startPath, highlight.startOffset, selectionStartPath, selectionStartOffset);
                            const highlightEndComparison = comparePaths(highlight.endPath, highlight.endOffset, selectionEndPath, selectionEndOffset);
                            
                            if (highlightStartComparison < 0) {
                                newHighlights.push({
                                    ...highlight,
                                    id: highlight.id + '_before_' + Date.now(),
                                    endPath: selectionStartPath,
                                    endOffset: selectionStartOffset
                                });
                            }
                            
                            if (highlightEndComparison > 0) {
                                newHighlights.push({
                                    ...highlight,
                                    id: highlight.id + '_after_' + Date.now(),
                                    startPath: selectionEndPath,
                                    startOffset: selectionEndOffset
                                });
                            }
                        } else {
                            newHighlights.push(highlight);
                        }
                    });
                    
                    newHighlights.push({
            id: 'highlight-' + Date.now(),
            text: text,
                        color: color,
                        pageNumber: parseInt(pageNum, 10),
                        startPath: selectionStartPath,
                        startOffset: selectionStartOffset,
                        endPath: selectionEndPath,
                        endOffset: selectionEndOffset,
                    });
                    
                    return newHighlights;
                });
            }
        } else {
            setHighlights(prev => [...prev, {
                id: 'highlight-' + Date.now(),
                text: text,
                color: color,
                pageNumber: parseInt(pageNum, 10),
                startPath: selectionStartPath,
                startOffset: selectionStartOffset,
                endPath: selectionEndPath,
                endOffset: selectionEndOffset,
            }]);
        }
    };

    const handleClearAllHighlights = () => {
        setHighlights([]);
    };

    const removeHighlightFromSelection = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        const text = selection.toString();
        if (!text.trim()) return;

        const startNode = range.startContainer;
        const startSearchElement = startNode.nodeType === Node.TEXT_NODE ? startNode.parentNode : startNode;
        const startPageElement = startSearchElement.closest('.react-pdf__Page');

        const endNode = range.endContainer;
        const endSearchElement = endNode.nodeType === Node.TEXT_NODE ? endNode.parentNode : endNode;
        const endPageElement = endSearchElement.closest('.react-pdf__Page');

        if (!startPageElement || !endPageElement || startPageElement !== endPageElement) {
            return;
        }

        const pageElement = startPageElement;
        const pageNum = pageElement.getAttribute('data-page-number');
        const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');

        if (!pageNum || !textLayerElement) {
            return;
        }

        const selectionStartPath = getPathTo(range.startContainer, textLayerElement);
        const selectionEndPath = getPathTo(range.endContainer, textLayerElement);
        const selectionStartOffset = range.startOffset;
        const selectionEndOffset = range.endOffset;

        setHighlights(prev => {
            const newHighlights = [];
            
            prev.forEach(highlight => {
                if (highlight.pageNumber !== parseInt(pageNum, 10)) {
                    newHighlights.push(highlight);
                    return;
                }
                
                const highlightStartVsSelectionEnd = comparePaths(highlight.startPath, highlight.startOffset, selectionEndPath, selectionEndOffset);
                const highlightEndVsSelectionStart = comparePaths(highlight.endPath, highlight.endOffset, selectionStartPath, selectionStartOffset);
                
                const hasOverlap = highlightStartVsSelectionEnd < 0 && highlightEndVsSelectionStart > 0;
                
                if (!hasOverlap) {
                    newHighlights.push(highlight);
                } else {
                    const highlightStartComparison = comparePaths(highlight.startPath, highlight.startOffset, selectionStartPath, selectionStartOffset);
                    const highlightEndComparison = comparePaths(highlight.endPath, highlight.endOffset, selectionEndPath, selectionEndOffset);
                    
                    if (highlightStartComparison < 0) {
                        newHighlights.push({
                            ...highlight,
                            id: highlight.id + '_before_' + Date.now(),
                            endPath: selectionStartPath,
                            endOffset: selectionStartOffset
                        });
                    }
                    
                    if (highlightEndComparison > 0) {
                        newHighlights.push({
                            ...highlight,
                            id: highlight.id + '_after_' + Date.now(),
                            startPath: selectionEndPath,
                            startOffset: selectionEndOffset
                        });
                    }
                }
            });
            
            return newHighlights;
        });
    };

    const handleStrikethroughSelected = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) return;

        const range = selection.getRangeAt(0);
        const text = selection.toString();
        if (!text.trim()) return;

        const startNode = range.startContainer;
        const startSearchElement = startNode.nodeType === Node.TEXT_NODE ? startNode.parentNode : startNode;
        const startPageElement = startSearchElement.closest('.react-pdf__Page');
    
        const endNode = range.endContainer;
        const endSearchElement = endNode.nodeType === Node.TEXT_NODE ? endNode.parentNode : endNode;
        const endPageElement = endSearchElement.closest('.react-pdf__Page');
    
        if (!startPageElement || !endPageElement || startPageElement !== endPageElement) {
            alert("Strikethrough across multiple pages is not supported. Please select text on a single page.");
            selection.removeAllRanges();
            return;
        }
    
        const pageElement = startPageElement;
        const pageNum = pageElement.getAttribute('data-page-number');
        const textLayerElement = pageElement.querySelector('.react-pdf__Page__textContent');

        if (!pageNum || !textLayerElement) {
            alert('Could not process the strikethrough. The page structure is unexpected.');
            return;
        }

        const selectionStartPath = getPathTo(range.startContainer, textLayerElement);
        const selectionEndPath = getPathTo(range.endContainer, textLayerElement);
        const selectionStartOffset = range.startOffset;
        const selectionEndOffset = range.endOffset;

        const overlappingStrikethroughs = strikethroughs.filter(strikethrough => {
            if (strikethrough.pageNumber !== parseInt(pageNum, 10)) return false;
            
            const strikethroughStartComparison = comparePaths(strikethrough.startPath, strikethrough.startOffset, selectionEndPath, selectionEndOffset);
            const strikethroughEndComparison = comparePaths(strikethrough.endPath, strikethrough.endOffset, selectionStartPath, selectionStartOffset);
            
            return strikethroughStartComparison < 0 && strikethroughEndComparison > 0;
        });

        if (overlappingStrikethroughs.length > 0) {
            setStrikethroughs(prev => {
                const newStrikethroughs = [];
                
                prev.forEach(strikethrough => {
                    const isOverlapping = overlappingStrikethroughs.some(o => o.id === strikethrough.id);
                    
                    if (!isOverlapping) {
                        newStrikethroughs.push(strikethrough);
                    } else {
                        const strikethroughStartComparison = comparePaths(strikethrough.startPath, strikethrough.startOffset, selectionStartPath, selectionStartOffset);
                        const strikethroughEndComparison = comparePaths(strikethrough.endPath, strikethrough.endOffset, selectionEndPath, selectionEndOffset);
                        
                        if (strikethroughStartComparison < 0) {
                            newStrikethroughs.push({
                                ...strikethrough,
                                id: strikethrough.id + '_before_' + Date.now(),
                                endPath: selectionStartPath,
                                endOffset: selectionStartOffset
                            });
                        }
                        
                        if (strikethroughEndComparison > 0) {
                            newStrikethroughs.push({
                                ...strikethrough,
                                id: strikethrough.id + '_after_' + Date.now(),
                                startPath: selectionEndPath,
                                startOffset: selectionEndOffset
                            });
                        }
                    }
                });
                
                return newStrikethroughs;
            });
        } else {
            setStrikethroughs(prev => [...prev, {
                id: 'strikethrough-' + Date.now(),
                text: text,
                pageNumber: parseInt(pageNum, 10),
                startPath: selectionStartPath,
                startOffset: selectionStartOffset,
                endPath: selectionEndPath,
                endOffset: selectionEndOffset,
            }]);
        }
    };

    const handleClearAllStrikethroughs = () => {
        setStrikethroughs([]);
    };

    useEffect(() => {
        const handleSelectionChange = () => {
            const selection = window.getSelection();
            setSelectedText(selection.toString().trim());
        };
        document.addEventListener('selectionchange', handleSelectionChange);
        return () => document.removeEventListener('selectionchange', handleSelectionChange);
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.pdf-zoom-dropdown-container')) {
                setShowZoomDropdown(false);
            }
            if (!e.target.closest('.pdf-tools-menu-container')) {
                setShowToolsMenu(false);
                setShowColorPicker(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useImperativeHandle(ref, () => ({
        triggerHighlight: handleHighlightSelected,
        triggerStrikethrough: handleStrikethroughSelected,
        removeHighlightFromSelection: removeHighlightFromSelection,
        clearAllHighlights: handleClearAllHighlights,
        clearAllStrikethroughs: handleClearAllStrikethroughs,
        hasSelection: () => !!selectedText,
        getSelectedText: () => selectedText,
    }));

    if (!docUrl) {
        return <div>Please provide a docUrl to load a PDF.</div>;
    }

    return (
        <div className="pdf-viewer-container">
            {showTools && (
                <>
                    {/* Toolbar */}
                    <div className="pdf-toolbar">
                        {/* Left Section */}
                        <div className="pdf-toolbar-left">
                            <button 
                                className={`pdf-toolbar-button ${showSidebar ? 'active' : ''}`}
                                onClick={() => setShowSidebar(!showSidebar)}
                                title={t('PdfToggleSidebar')}
                            >
                                <img 
                                    src={sidebarToggleIcon} 
                                    alt={t('PdfToggleSidebar')}
                                    width="16"
                                    height="16"
                                />
                            </button>
                            
                            <button 
                                className="pdf-toolbar-button"
                                onClick={toggleSearch}
                                title={t('PdfFind')}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M11.5 10h-.8l-.3-.3c1-1.1 1.6-2.6 1.6-4.2C12 2.5 9.5 0 6.5 0S1 2.5 1 5.5 3.5 11 6.5 11c1.6 0 3.1-.6 4.2-1.6l.3.3v.8l5 5 1.5-1.5-5-5zm-5 0C4 10 2 8 2 5.5S4 1 6.5 1 11 3 11 5.5 9 10 6.5 10z"/>
                                </svg>
                            </button>

                            <div className="pdf-toolbar-separator"></div>

                            <button 
                                className="pdf-toolbar-button"
                                onClick={previousPage}
                                disabled={pageNumber <= 1}
                                title={t('PdfPreviousPage')}
                            >
                                <img 
                                    src={pageDownIcon} 
                                    alt={t('PdfPreviousPage')}
                                    width="16"
                                    height="16"
                                />
                            </button>

                            <button 
                                className="pdf-toolbar-button"
                                onClick={nextPage}
                                disabled={pageNumber >= numPages}
                                title={t('PdfNextPage')}
                            >
                                <img 
                                    src={pageUpIcon} 
                                    alt={t('PdfNextPage')}
                                    width="16"
                                    height="16"
                                />
                            </button>

                            <div className="pdf-page-controls">
                                <span>{t('PdfPage')}:</span>
                                <input
                                    type="number"
                                    min="1"
                                    max={numPages || 1}
                                    value={pageNumber}
                                    onChange={(e) => goToPage(e.target.value)}
                                    className="pdf-page-input"
                                />
                                <span>{t('PdfOf')} {numPages || 0}</span>
                            </div>
                        </div>

                        {/* Center Section */}
                        <div className="pdf-toolbar-center">
                            <button 
                                className="pdf-toolbar-button"
                                onClick={zoomOut}
                                title={t('PdfZoomOut')}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M3 7h8v2H3z"/>
                                </svg>
                            </button>

                            <button 
                                className="pdf-toolbar-button"
                                onClick={zoomIn}
                                title={t('PdfZoomIn')}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M7 3h2v4h4v2H9v4H7V9H3V7h4z"/>
                                </svg>
                            </button>

                            <div className="pdf-zoom-dropdown-container">
                                <button 
                                    className="pdf-zoom-button"
                                    onClick={() => setShowZoomDropdown(!showZoomDropdown)}
                                >
                                    {zoomMode === 'auto' && t('PdfAutomaticZoom')}
                                    {zoomMode === 'actual' && t('PdfActualSize')}
                                    {zoomMode === 'fit' && t('PdfPageFit')}
                                    {zoomMode === 'width' && t('PdfPageWidth')}
                                    {zoomMode === 'custom' && `${Math.round(scale * 100)}%`}
                                    <svg width="12" height="12" viewBox="0 0 12 12" style={{marginLeft: '4px'}}>
                                        <path fill="currentColor" d="M2 4l4 4 4-4z"/>
                                    </svg>
                                </button>

                                {showZoomDropdown && (
                                    <div className="pdf-zoom-dropdown">
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('auto')}>{t('PdfAutomaticZoom')}</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('actual')}>{t('PdfActualSize')}</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('fit')}>{t('PdfPageFit')}</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('width')}>{t('PdfPageWidth')}</div>
                                        <div className="pdf-zoom-separator"></div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('50')}>50%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('75')}>75%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('100')}>100%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('125')}>125%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('150')}>150%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('200')}>200%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('300')}>300%</div>
                                        <div className="pdf-zoom-option" onClick={() => setZoomLevel('400')}>400%</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Section */}
                        <div className="pdf-toolbar-right">
                            {/* Advanced Tools */}
                            {advancedTools && (
                                <>
                                    {/* Highlight Button with Color Picker */}
                                    <div className="pdf-tools-menu-container">
                                        <button 
                                            className={`pdf-toolbar-button ${activeTool === 'highlight' ? 'active' : ''}`}
                                            onClick={() => setActiveTool(activeTool === 'highlight' ? null : 'highlight')}
                                            title={t('PdfHighlightText')}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16">
                                                <path fill="currentColor" d="M14 3l-1-1-5 5-1-1-3 3 2 2 3-3-1-1 5-5zM3 14h10v2H3z"/>
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Color Picker Button */}
                                    <div className="pdf-tools-menu-container">
                                        <button 
                                            className="pdf-toolbar-button"
                                            onClick={() => setShowColorPicker(!showColorPicker)}
                                            title={t('PdfHighlightColor')}
                                            style={{ position: 'relative' }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16">
                                                <circle cx="8" cy="8" r="6" fill={
                                                    highlightColor === 'yellow' ? '#ffff00' :
                                                    highlightColor === 'green' ? '#00ff00' :
                                                    highlightColor === 'pink' ? '#ffb6c1' :
                                                    '#add8e6'
                                                } stroke="currentColor" strokeWidth="1"/>
                                            </svg>
                                        </button>

                                        {showColorPicker && (
                                            <div className="pdf-tools-menu" style={{ minWidth: '120px' }}>
                                                <div 
                                                    className="pdf-tools-option" 
                                                    onClick={() => { setHighlightColor('yellow'); setShowColorPicker(false); }}
                                                >
                                                    <div style={{ width: '16px', height: '16px', backgroundColor: '#ffff00', marginRight: '8px', border: '1px solid #666' }}></div>
                                                    {t('PdfYellow')}
                                                </div>
                                                <div 
                                                    className="pdf-tools-option" 
                                                    onClick={() => { setHighlightColor('green'); setShowColorPicker(false); }}
                                                >
                                                    <div style={{ width: '16px', height: '16px', backgroundColor: '#00ff00', marginRight: '8px', border: '1px solid #666' }}></div>
                                                    {t('PdfGreen')}
                                                </div>
                                                <div 
                                                    className="pdf-tools-option" 
                                                    onClick={() => { setHighlightColor('pink'); setShowColorPicker(false); }}
                                                >
                                                    <div style={{ width: '16px', height: '16px', backgroundColor: '#ffb6c1', marginRight: '8px', border: '1px solid #666' }}></div>
                                                    {t('PdfPink')}
                                                </div>
                                                <div 
                                                    className="pdf-tools-option" 
                                                    onClick={() => { setHighlightColor('blue'); setShowColorPicker(false); }}
                                                >
                                                    <div style={{ width: '16px', height: '16px', backgroundColor: '#add8e6', marginRight: '8px', border: '1px solid #666' }}></div>
                                                    {t('PdfBlue')}
                                                </div>
                                                <div className="pdf-tools-separator"></div>
                                                <div 
                                                    className="pdf-tools-option" 
                                                    onClick={() => { setHighlightColor('remove'); setShowColorPicker(false); }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                        <path fill="currentColor" d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2"/>
                                                    </svg>
                                                    {t('PdfRemoveHighlight')}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Strikethrough Button */}
                                    <button 
                                        className={`pdf-toolbar-button ${activeTool === 'strikethrough' ? 'active' : ''}`}
                                        onClick={() => setActiveTool(activeTool === 'strikethrough' ? null : 'strikethrough')}
                                        title={t('PdfStrikethroughText')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16">
                                            <path fill="currentColor" d="M2 8h12v1H2V8zm3-4h6c1.1 0 2 .9 2 2v1h-2V6c0-.55-.45-1-1-1H5c-.55 0-1 .45-1 1v1H2V6c0-1.1.9-2 2-2zm0 6h6c.55 0 1 .45 1 1v1c0 .55-.45 1-1 1H5c-.55 0-1-.45-1-1v-1c0-.55.45-1 1-1z"/>
                                        </svg>
                                    </button>

                                    <div className="pdf-toolbar-separator"></div>
                                </>
                            )}

                            <div className="pdf-tools-menu-container">
                                <button 
                                    className="pdf-toolbar-button"
                                    onClick={() => setShowToolsMenu(!showToolsMenu)}
                                    title={t('PdfTools')}
                                >
                                    <img 
                                        src={secondaryToolbarToggleIcon} 
                                        alt={t('PdfTools')}
                                        width="16"
                                        height="16"
                                    />
                                </button>

                                {showToolsMenu && (
                                    <div className="pdf-tools-menu">
                                        <div className="pdf-tools-option" onClick={goToFirstPage}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M3 2h2v12H3V2zm10 10L9 8l4-4z"/>
                                            </svg>
                                            {t('PdfGoToFirstPage')}
                                        </div>
                                        <div className="pdf-tools-option" onClick={goToLastPage}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M11 2h2v12h-2V2zM3 4l4 4-4 4z"/>
                                            </svg>
                                            {t('PdfGoToLastPage')}
                                        </div>
                                        <div className="pdf-tools-separator"></div>
                                        <div className="pdf-tools-option" onClick={rotateClockwise}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M13 8c0-2.8-2.2-5-5-5V1L5 4l3 3V5c1.7 0 3 1.3 3 3s-1.3 3-3 3c-.8 0-1.5-.3-2-.8l-1.4 1.4C5.6 12.5 6.8 13 8 13c2.8 0 5-2.2 5-5z"/>
                                            </svg>
                                            {t('PdfRotateClockwise')}
                                        </div>
                                        <div className="pdf-tools-option" onClick={rotateAntiClockwise}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M3 8c0 2.8 2.2 5 5 5v2l3-3-3-3v2c-1.7 0-3-1.3-3-3s1.3-3 3-3c.8 0 1.5.3 2 .8l1.4-1.4C10.4 3.5 9.2 3 8 3c-2.8 0-5 2.2-5 5z"/>
                                            </svg>
                                            {t('PdfRotateAntiClockwise')}
                                        </div>
                                        <div className="pdf-tools-separator"></div>
                                        <div className="pdf-tools-option disabled">
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M8 1l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z"/>
                                            </svg>
                                            {t('PdfTextSelectionTool')}
                                        </div>
                                        <div className="pdf-tools-option disabled">
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M8 2c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v6h12V8c0-1.1-.9-2-2-2h-2V4c0-1.1-.9-2-2-2z"/>
                                            </svg>
                                            {t('PdfHandTool')}
                                        </div>
                                        <div className="pdf-tools-separator"></div>
                                        <div className="pdf-tools-option disabled">
                                            <svg width="16" height="16" viewBox="0 0 16 16" style={{marginRight: '8px'}}>
                                                <path fill="currentColor" d="M8 1C4.1 1 1 4.1 1 8s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7zm1 11H7V7h2v5zm0-6H7V4h2v2z"/>
                                            </svg>
                                            {t('PdfDocumentProperties')}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Search Bar */}
                    {showSearchBar && (
                        <div className="pdf-search-bar">
                            <label>{t('PdfFind')}:</label>
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        if (e.shiftKey) {
                                            findPrevious();
                                        } else {
                                            findNext();
                                        }
                                    }
                                }}
                                className="pdf-search-input"
                                placeholder={t('PdfSearchInDocument')}
                            />
                            {searchMatches.length > 0 && (
                                <span style={{ fontSize: '12px', color: '#fff', whiteSpace: 'nowrap' }}>
                                    {currentMatchIndex + 1} / {searchMatches.length}
                                </span>
                            )}
                            {searchText && searchMatches.length === 0 && (
                                <span style={{ fontSize: '12px', color: '#ff6b6b', whiteSpace: 'nowrap' }}>
                                    {t('PdfNoMatches')}
                                </span>
                            )}
                            <button 
                                className="pdf-search-button" 
                                onClick={findPrevious}
                                disabled={searchMatches.length === 0}
                                title={t('PdfPrevious')}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M10 12L6 8l4-4z"/>
                                </svg>
                            </button>
                            <button 
                                className="pdf-search-button" 
                                onClick={findNext}
                                disabled={searchMatches.length === 0}
                                title={t('PdfNext')}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M6 4l4 4-4 4z"/>
                                </svg>
                            </button>
                            <label className="pdf-search-checkbox">
                                <input
                                    type="checkbox"
                                    checked={highlightAll}
                                    onChange={(e) => setHighlightAll(e.target.checked)}
                                />
                                {t('PdfHighlightAll')}
                            </label>
                            <label className="pdf-search-checkbox">
                                <input
                                    type="checkbox"
                                    checked={matchCase}
                                    onChange={(e) => setMatchCase(e.target.checked)}
                                />
                                {t('PdfMatchCase')}
                            </label>
                            <button 
                                className="pdf-search-close"
                                onClick={toggleSearch}
                                title={t('PdfClose')}
                            >
                                ×
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Main Content Area */}
            <div className="pdf-content-area">
                {/* Thumbnail Sidebar */}
                {showSidebar && (
                    <div className="pdf-sidebar">
                        <div className="pdf-sidebar-tabs">
                            <button className="pdf-sidebar-tab active">
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M2 2h4v12H2V2zm5 0h7v12H7V2z"/>
                                </svg>
                            </button>
                            <button className="pdf-sidebar-tab">
                                <svg width="16" height="16" viewBox="0 0 16 16">
                                    <path fill="currentColor" d="M3 3h10v2H3V3zm0 4h10v2H3V7zm0 4h10v2H3v-2z"/>
                                </svg>
                            </button>
                        </div>
                        <div className="pdf-sidebar-content">
                            {pdfDocument && Array.from(new Array(numPages || 0), (el, index) => (
                                <div
                                    key={`thumb_${index + 1}`}
                                    className={`pdf-thumbnail ${pageNumber === index + 1 ? 'active' : ''}`}
                                    onClick={() => goToPage(index + 1)}
                                >
                                    <div className="pdf-thumbnail-canvas">
                                        <Page
                                            pdf={pdfDocument}
                                            pageNumber={index + 1}
                                            width={120}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                    </div>
                                    <div className="pdf-thumbnail-label">{t('PdfPage')} {index + 1}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* PDF Viewport */}
                <div 
                    className={scrolling ? "tw-max-h-[400px] tw-overflow-auto" : 'pdf-viewport'}
                    ref={pdfContainerRef}
                    onMouseUp={() => {
                        if (!advancedTools || !activeTool) return;
                        
                        setTimeout(() => {
                            if (activeTool === 'highlight') {
                                if (highlightColor === 'remove') {
                                    removeHighlightFromSelection();
                                } else {
                                    handleHighlightSelected(highlightColor);
                                }
                            } else if (activeTool === 'strikethrough') {
                                handleStrikethroughSelected();
                            }
                        }, 10);
                    }}
                >
                    <Document
                        file={stableDocUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        options={options}
                        scale={scale}
                    >
                        <div className="page-container" ref={pageRef} style={{ position: 'relative' }}>
                            <Page
                                pageNumber={pageNumber}
                                width={zoomMode === 'auto' ? (autoWidth || width - 3 || undefined) : (width - 3 || undefined)}
                                height={zoomMode === 'auto' ? (autoHeight || height) : height}
                                rotate={pageRotations[pageNumber] || 0}
                                onRenderSuccess={() => {
                                    // Recalculate auto zoom after page renders
                                    if (zoomMode === 'auto') {
                                        calculateAutoZoom();
                                    }
                                }}
                            />
                            
                            {/* Inline Highlight Layer */}
                            <div className="pdf-highlight-layer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                                {highlightBoxes
                                    .filter(box => box.pageNumber === pageNumber || !box.pageNumber)
                                    .map(box => {
                                        const colorMap = {
                                            yellow: 'rgba(255, 255, 0, 0.4)',
                                            green: 'rgba(0, 255, 0, 0.4)',
                                            pink: 'rgba(255, 182, 193, 0.4)',
                                            blue: 'rgba(173, 216, 230, 0.4)',
                                            remove: 'transparent'
                                        };
                                        return (
                                            <div
                                                key={box.key}
                                                style={{
                                                    position: 'absolute',
                                                    top: `${box.top}px`,
                                                    left: `${box.left}px`,
                                                    width: `${box.width}px`,
                                                    height: `${box.height}px`,
                                                    backgroundColor: colorMap[box.color] || colorMap.yellow,
                                                    zIndex: 1,
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                        );
                                    })
                                }
                            </div>

                            {/* Inline Strikethrough Layer */}
                            <div className="pdf-strikethrough-layer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                                {strikethroughBoxes
                                    .filter(box => box.pageNumber === pageNumber || !box.pageNumber)
                                    .map(box => (
                                        <div
                                            key={box.key}
                                            style={{
                                                position: 'absolute',
                                                top: `${box.top + box.height / 2}px`,
                                                left: `${box.left}px`,
                                                width: `${box.width}px`,
                                                height: '2px',
                                                backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                                zIndex: 2,
                                                pointerEvents: 'none',
                                            }}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    </Document>
                </div>
            </div>
        </div>
    );
});

TbsPdfReader.displayName = 'TbsPdfReader';

export default TbsPdfReader;
